import { FC } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';

import {
    FILTER_CHIP_PREFIXES,
    FILTER_VALUES_WITH_LABELS,
    FULL_PRICE_FIELD_NAME,
    MONTHLY_PRICE_FIELD_NAME,
    PLAN_DATA_SIZE_FIELDS,
    PLAN_PRICE_FIELD_NAME,
} from '../../constants';
import { SearchFacet, SearchFilters, SearchFilterValue } from '../../model';
import { SEARCH_TEST_ID } from '../../test/test-ids';
import { cleanupFiltersWithoutResults } from '../../utils/filtersCleanup';
import { isFilterHierarchy, isFilterRange, isFilterTerms } from '../../utils/typeGuards';
import { SelectedFilterItem, SelectedFilters } from '../SelectedFilters/SelectedFilters';

type Props = {
    facets: SearchFacet[];
    filters: SearchFilters;
    onRemoveFilter: (fieldName: string, newFilterValue?: SearchFilterValue) => void;
    onRemoveAllFilters: () => void;
};

export const ProductsSelectedFilters: FC<Props> = ({ facets, filters, onRemoveFilter, onRemoveAllFilters }) => {
    const { t } = useTranslations();
    const findFacetByKey = (fieldName: string) => facets.find(({ key }) => key === fieldName);

    const cleanedUpFilters = cleanupFiltersWithoutResults(facets, filters);

    const filterFieldNames = Object.keys(cleanedUpFilters).filter((fieldName) => fieldName !== 'categoryPath');

    const mappedSelectedFilterItems = filterFieldNames.reduce<SelectedFilterItem[]>((acc, fieldName) => {
        const filter = cleanedUpFilters[fieldName];
        const facet = findFacetByKey(fieldName);

        // Handle _and special case
        if (fieldName === '_and' && isFilterTerms(filter)) {
            const andFilters = filter.flatMap((andFilter) => {
                return Object.entries(andFilter).flatMap(([nestedField, value]) => {
                    const nestedFacet = findFacetByKey(nestedField);

                    return [
                        {
                            value: { type: 'terms' as const, fieldName: nestedField, term: value },
                            label: FILTER_VALUES_WITH_LABELS.includes(value)
                                ? `${nestedFacet?.label}: ${value}`
                                : value,
                        },
                    ];
                });
            });

            return [...acc, ...andFilters];
        }

        if (fieldName === 'weightGrams' && isFilterRange(filter)) {
            const convertToKg = (value: number) => +(value / 1000).toFixed(3);

            return [
                ...acc,
                {
                    value: { type: 'range', fieldName },
                    label: `${convertToKg(filter.gte)} - ${convertToKg(filter.lte)} ${t['search.component.weight_filter.unit']}`,
                },
            ];
        }

        if (isFilterHierarchy(filter)) {
            const termsItems: SelectedFilterItem[] = filter.terms.map((term) => {
                const termParts = term.split(' > ');

                return {
                    value: { type: 'hierarchy', fieldName, term },
                    label: termParts[termParts.length - 1],
                };
            });

            return [...acc, ...termsItems];
        } else if (
            isFilterRange(filter) &&
            (fieldName === MONTHLY_PRICE_FIELD_NAME ||
                fieldName === FULL_PRICE_FIELD_NAME ||
                fieldName === PLAN_PRICE_FIELD_NAME)
        ) {
            const priceUnit = t['search.component.price_filter.unit'];
            const item: SelectedFilterItem = {
                value: { type: 'range', fieldName },
                label: `${filter.gte} ${priceUnit}  - ${filter.lte} ${priceUnit}`,
            };

            return [...acc, item];
        } else if (isFilterRange(filter) && PLAN_DATA_SIZE_FIELDS.includes(fieldName as any)) {
            const dataSizeUnit = t['search.component.data.gb_unit'];
            const prefix = FILTER_CHIP_PREFIXES[fieldName] ? t[FILTER_CHIP_PREFIXES[fieldName]] : '';
            const fromLabel = filter.gte;
            const toLabel = filter.lte >= 9999 ? t['search.component.data.unlimited_label'] : filter.lte;

            const item: SelectedFilterItem = {
                value: { type: 'range', fieldName },
                label: `${prefix}${fromLabel} ${dataSizeUnit} - ${toLabel} ${dataSizeUnit}`,
            };

            return [...acc, item];
        } else if (isFilterRange(filter)) {
            const item: SelectedFilterItem = {
                value: { type: 'range', fieldName },
                label: `${filter.gte} - ${filter.lte}`,
            };

            return [...acc, item];
        } else if (isFilterTerms(filter)) {
            const termsItems: SelectedFilterItem[] = filter.map((term) => ({
                value: { type: 'terms', fieldName, term },
                label: [FILTER_VALUES_WITH_LABELS.includes(term) ? facet?.label : '', term].filter(Boolean).join(': '),
            }));

            return [...acc, ...termsItems];
        }

        return acc;
    }, []);

    const handleRemoveFilter = (removedItem: SelectedFilterItem['value']) => {
        const initialFilter = cleanedUpFilters[removedItem.fieldName];
        if (removedItem.type === 'range') {
            onRemoveFilter(removedItem.fieldName, undefined);
        } else if (removedItem.type === 'terms' && isFilterTerms(initialFilter)) {
            const remainingTerms = initialFilter.filter((term) => term !== removedItem.term);
            onRemoveFilter(removedItem.fieldName, remainingTerms.length > 0 ? remainingTerms : undefined);
        } else if (removedItem.type === 'hierarchy' && isFilterHierarchy(initialFilter)) {
            const remainingTerms = initialFilter.terms.filter((term) => term !== removedItem.term);
            onRemoveFilter(
                removedItem.fieldName,
                remainingTerms.length > 0 ? { level: 0, terms: remainingTerms } : undefined,
            );
        }
    };

    return (
        <SelectedFilters
            filters={mappedSelectedFilterItems}
            onRemove={handleRemoveFilter}
            onRemoveAll={onRemoveAllFilters}
            dataTest={SEARCH_TEST_ID.RESULTS_PRODUCTS_SELECTED_FILTERS}
        />
    );
};
